import React, { useEffect, useMemo, useState } from 'react'
import Pie, { ProvidedProps, PieArcDatum } from '@visx/shape/lib/shapes/Pie'
import { scaleOrdinal } from '@visx/scale'
import { Group } from '@visx/group'
import { GradientPinkBlue } from '@visx/gradient'
import letterFrequency, { LetterFrequency } from '@visx/mock-data/lib/mocks/letterFrequency'
import browserUsage, { BrowserUsage as Browsers } from '@visx/mock-data/lib/mocks/browserUsage'
import { animated, useTransition, interpolate } from 'react-spring'
import merge from 'lodash/merge'
import { chartColors } from '../../theming'
import { LegendItem, LegendLabel, LegendOrdinal } from '@visx/legend'
import { useTooltip, useTooltipInPortal, TooltipWithBounds } from '@visx/tooltip'
import { localPoint } from '@visx/event'
import { Column, Title, Row } from '../../../../index'
// data and types
type BrowserNames = keyof Browsers

interface BrowserUsage {
    label: BrowserNames
    usage: number
}

const legendGlyphSize = 20

const letters: LetterFrequency[] = letterFrequency.slice(0, 4)
const browserNames = Object.keys(browserUsage[0]).filter((k) => k !== 'date') as BrowserNames[]
const browsers: BrowserUsage[] = browserNames.map((name) => ({
    label: name,
    usage: Number(browserUsage[0][name]),
}))

// accessor functions
const usage = (d: BrowserUsage) => d.usage
const frequency = (d: LetterFrequency) => d.frequency

// color scales

const getLetterFrequencyColor = scaleOrdinal({
    domain: letters.map((l) => l.letter),
    range: ['rgba(93,30,91,1)', 'rgba(93,30,91,0.8)', 'rgba(93,30,91,0.6)', 'rgba(93,30,91,0.4)'],
})

const defaultMargin = { top: 20, right: 20, bottom: 20, left: 20 }

export type PieProps = {
    width: number
    height: number
    margin?: any
    animate?: boolean
    hideLabel?: boolean
    showPieCount?: boolean
    showPieCountOnFocus?: boolean
    pieCalloutTextColor?: string
    setLegend: any
    pieDiameter: number
    onDataItemClick?: (d) => any
    valueDisplayFormat?: (d) => string
    data: { label: string; count: number; color?: string }[]
}

export default function Example({
    showPieCount,
    showPieCountOnFocus,
    setLegend,
    hideLabel,
    data,
    width,
    height,
    margin = {},
    animate = true,
    onDataItemClick,
    pieDiameter,
    valueDisplayFormat,
    pieCalloutTextColor,
}: PieProps) {
    const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<any>()
    const _margin = merge(defaultMargin, margin)
    const [selectedData, setSelectedData] = useState<string | null>(null)
    const [selectedAlphabetLetter, setSelectedAlphabetLetter] = useState<string | null>(null)

    const colors = useMemo(() => {
        if (data[0].color) {
            return data.map((d) => d.color)
        }

        return chartColors
            .map((value) => ({ value, sort: Math.random() }))
            .sort((a, b) => a.sort - b.sort)
            .map(({ value }) => value)
    }, [data])

    const colorScale = scaleOrdinal({
        domain: data,
        range: colors,
    })

    const calloutNumber = useMemo(() => {
        if (selectedData && (showPieCount || showPieCountOnFocus)) {
            return data.find((d) => d.label === selectedData).count
        }
        if (showPieCount) {
            return data?.reduce((total, d) => (total += d.count), 0) ?? 0
        }
        return null
    }, [data, selectedData])

    useEffect(() => {
        // @ts-ignore
        setLegend(
            <LegendOrdinal scale={colorScale} labelFormat={(label) => `${label?.toString().toUpperCase()}`}>
                {(labels) => (
                    <div className={'r-chart-legend-container'}>
                        {labels.map((label, i) => {
                            const x = labels

                            if (typeof label.datum === 'object') {
                                return null
                            }
                            const text: string = label.datum
                            return (
                                <LegendItem
                                    onClick={() => {
                                        setSelectedData(selectedData === text ? null : text)
                                    }}
                                    key={`legend-quantile-${i}`}
                                    margin='5px 5px'
                                >
                                    <svg width={legendGlyphSize} height={legendGlyphSize}>
                                        <rect fill={label.value} width={legendGlyphSize} height={legendGlyphSize} />
                                    </svg>
                                    <LegendLabel
                                        style={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            margin: '0 0 0 4px',
                                        }}
                                        align='left'
                                    >
                                        {text}
                                    </LegendLabel>
                                </LegendItem>
                            )
                        })}
                    </div>
                )}
            </LegendOrdinal>,
        )
    }, [selectedData, data])

    // If you don't want to use a Portal, simply replace `TooltipInPortal` below with
    // `Tooltip` or `TooltipWithBounds` and remove `containerRef`
    const { containerRef, TooltipInPortal } = useTooltipInPortal({
        // use TooltipWithBounds
        detectBounds: true,
        // when tooltip containers are scrolled, this will correctly update the Tooltip position
        scroll: true,
    })

    const handleMouseOver = (event, datum) => {
        const coords = localPoint(event.target.ownerSVGElement, event)
        showTooltip({
            tooltipLeft: coords.x,
            tooltipTop: coords.y,
            tooltipData: datum,
        })
    }

    if (width < 10) return null

    const innerWidth = width - _margin.left - _margin.right
    const innerHeight = height - _margin.top - _margin.bottom
    const radius = Math.min(innerWidth, innerHeight) / 2
    const centerY = innerHeight / 2
    const centerX = innerWidth / 2
    const donutThickness = pieDiameter || 50

    const calloutFontSize =
        calloutNumber >= 1000000 ? 25 : calloutNumber >= 100000 ? 30 : calloutNumber >= 10000 ? 30 : 40

    return (
        <svg width={width} height={height}>
            <filter id='callOutShadow' colorInterpolationFilters='sRGB'>
                <feColorMatrix
                    in='SourceAlpha'
                    type='matrix'
                    values='-1 0 0 0 1, 0 -1 0 0 1, 0 0 -1 0 1, 0 0 0 1 0'
                    result='matrix'
                />
                <feGaussianBlur in='matrix' stdDeviation='1' result='blur' />
                <feComposite in='SourceGraphic' in2='blur' operator='over' />
                {/*<feDropShadow dx='0.5' dy='0.5' stdDeviation='1' />*/}
                {/*<feDropShadow dx='2' dy='2' stdDeviation='3' floodOpacity='0.8' />*/}
            </filter>
            {/*<GradientPinkBlue id='visx-pie-gradient' />*/}
            {/*<rect rx={14} width={width} height={height} fill="url('#visx-pie-gradient')" />*/}
            <Group top={centerY + _margin.top} left={centerX + _margin.left}>
                <Pie
                    data={selectedData ? data.filter(({ label }) => label === selectedData) : data}
                    pieValue={(d) => d.count}
                    outerRadius={radius}
                    innerRadius={radius - donutThickness}
                    cornerRadius={0}
                    padAngle={0.005}
                >
                    {(pie) => (
                        <AnimatedPie<any>
                            {...pie}
                            onMouseOver={handleMouseOver}
                            onMouseOut={hideTooltip}
                            hideLabel={hideLabel}
                            animate={animate}
                            getKey={(arc) => arc.data.label}
                            onClickDatum={({ data: { label } }) => {
                                if (onDataItemClick) {
                                    onDataItemClick(data.find((d) => d.label === label))
                                }
                                animate && setSelectedData(selectedData && selectedData === label ? null : label)
                            }}
                            getColor={(arc) => colorScale(arc.data.label)}
                        />
                    )}
                </Pie>
                {calloutNumber !== null ? (
                    <g filter={'url(#callOutShadow)'}>
                        <text
                            textAnchor='middle'
                            x={0}
                            y={15}
                            fill={pieCalloutTextColor || '#000'}
                            fontSize={calloutFontSize}
                            fontWeight={600}
                            pointerEvents='none'
                        >
                            {valueDisplayFormat ? valueDisplayFormat(calloutNumber) : calloutNumber}
                        </text>
                    </g>
                ) : null}

                {tooltipOpen && (
                    <TooltipInPortal
                        // set this to random so it correctly updates with parent bounds
                        key={Math.random()}
                        top={tooltipTop}
                        left={tooltipLeft}
                    >
                        <Row className={'r-relay-container'}>
                            <Column alignCenter>
                                <Title type={'uppercase'} size={'sm'}>
                                    {tooltipData.label}
                                </Title>
                                <Title size={'lg'}>
                                    {valueDisplayFormat ? valueDisplayFormat(tooltipData.count) : ''}
                                </Title>
                            </Column>
                        </Row>
                    </TooltipInPortal>
                )}
            </Group>
        </svg>
    )
}

// react-spring transition definitions
type AnimatedStyles = { startAngle: number; endAngle: number; opacity: number }

const fromLeaveTransition = ({ endAngle }: PieArcDatum<any>) => ({
    // enter from 360° if end angle is > 180°
    startAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
    endAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
    opacity: 0,
})
const enterUpdateTransition = ({ startAngle, endAngle }: PieArcDatum<any>) => ({
    startAngle,
    endAngle,
    opacity: 1,
})

type AnimatedPieProps<Datum> = ProvidedProps<Datum> & {
    animate?: boolean
    getKey: (d: PieArcDatum<Datum>) => string
    getColor: (d: PieArcDatum<Datum>) => string
    onClickDatum: (d: PieArcDatum<Datum>) => void
    delay?: number
    hideLabel?: boolean
    onMouseOut: (any) => any
    onMouseOver: (event: any, datum: any) => any
}

function AnimatedPie<Datum>({
    hideLabel,
    animate,
    arcs,
    path,
    getKey,
    getColor,
    onClickDatum,
    onMouseOver,
    onMouseOut,
}: AnimatedPieProps<Datum>) {
    const transitions = useTransition<PieArcDatum<Datum>, AnimatedStyles>(arcs, {
        from: animate ? fromLeaveTransition : enterUpdateTransition,
        enter: enterUpdateTransition,
        update: enterUpdateTransition,
        leave: animate ? fromLeaveTransition : enterUpdateTransition,
        keys: getKey,
    })
    return transitions((props, arc, { key }) => {
        const [centroidX, centroidY] = path.centroid(arc)
        const hasSpaceForLabel = arc.endAngle - arc.startAngle >= 0.1

        const _hideLabel = hideLabel && arcs.length > 1

        return (
            <g
                onMouseOut={onMouseOut}
                onMouseOver={(e) => {
                    onMouseOver(e, arc.data)
                }}
                key={key}
            >
                <filter id='shadow' colorInterpolationFilters='sRGB'>
                    <feDropShadow dx='0.5' dy='0.5' stdDeviation='1' />
                    {/*<feDropShadow dx='2' dy='2' stdDeviation='3' floodOpacity='0.8' />*/}
                </filter>
                <animated.path
                    // compute interpolated path d attribute from intermediate angle values
                    d={interpolate([props.startAngle, props.endAngle], (startAngle, endAngle) =>
                        path({
                            ...arc,
                            startAngle,
                            endAngle,
                        }),
                    )}
                    fill={getColor(arc)}
                    onClick={() => onClickDatum(arc)}
                    onTouchStart={() => onClickDatum(arc)}
                />
                {hasSpaceForLabel && !_hideLabel && (
                    <animated.g filter={'url(#shadow)'} style={{ opacity: props.opacity }}>
                        <text
                            fill='whitesmoke'
                            x={centroidX}
                            y={centroidY}
                            dy='.33em'
                            fontSize={14}
                            textAnchor='middle'
                            pointerEvents='none'
                        >
                            {getKey(arc)}
                        </text>
                    </animated.g>
                )}
            </g>
        )
    })
}
